<script setup lang="ts" generic="V">
import IconChevronUp from "@dsk/icons/LeChevronUp.vue";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { computed, watch } from "vue";

import type { LeSelectOption } from "@/types";

interface Props {
  size?: "small" | "large";
  hasError?: boolean;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  width?: string;
  options: LeSelectOption<V>[];
  modelValue?: V;
  dropUp?: boolean;
  maxHeight?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: "large",
  maxHeight: "60vh",
  label: undefined,
  placeholder: undefined,
  width: undefined,
  modelValue: undefined
});

const emit = defineEmits<{
  (event: "update:modelValue", value: V | undefined): void;
}>();

defineOptions({ inheritAttrs: false });

const COMPONENT_CLASS = "le-select";

// When using a generic + defaults, a ts2339 is triggered by v-bind(props.whatever) the component's stylesheet.
// Therefore we proxy them in computed properties.
const width = computed<string | undefined>(() => props.width);
const maxHeight = computed<string | undefined>(() => props.maxHeight);

const selectedOption = computed<LeSelectOption<V | undefined> | undefined>({
  get() {
    return props.options.find((option) => option.value === props.modelValue);
  },
  set(option) {
    emit("update:modelValue", option?.value);
  }
});

watch(
  () => props.options,
  (options) => {
    // Need to compare the options object, as the selectedOption can be undefined,
    // Making it impossible to reset the selected-option.
    const foundSelectedOption = options.find((option) => option === selectedOption.value);
    if (!foundSelectedOption) {
      selectedOption.value = undefined;
    }
  }
);
</script>

<template>
  <Component :is="props.label ? 'label' : 'div'" :class="`${COMPONENT_CLASS}__container`">
    <span v-if="props.label" :class="`${COMPONENT_CLASS}__label`">{{ props.label }}</span>
    <Listbox
      v-slot="{ disabled: listboxDisabled, open }"
      v-bind="$attrs"
      v-model="selectedOption"
      :disabled="disabled"
      @keydown="$event.stopPropagation()"
    >
      <div
        :class="{
          [COMPONENT_CLASS]: true,
          [`${COMPONENT_CLASS}--${size}`]: true,
          [`${COMPONENT_CLASS}--disabled`]: disabled || listboxDisabled,
          [`${COMPONENT_CLASS}--has-error`]: hasError,
          [`${COMPONENT_CLASS}--open`]: open
        }"
      >
        <ListboxButton
          :class="{
            [`${COMPONENT_CLASS}__button`]: true,
            [`${COMPONENT_CLASS}__button--${size}`]: true,
            [`${COMPONENT_CLASS}__button--has-error`]: hasError
          }"
        >
          <div v-if="$slots.icon" :class="`${COMPONENT_CLASS}__button-prefix-icon`">
            <slot name="icon"></slot>
          </div>
          <span :class="`${COMPONENT_CLASS}__button-text`">
            <!-- &nbsp; is to make sure the element has a height, when no text is available... -->
            {{ selectedOption?.text ?? placeholder ?? "&nbsp;" }}
          </span>
          <IconChevronUp :class="`${COMPONENT_CLASS}__button-arrow-icon`" role="presentation" />
        </ListboxButton>

        <Transition name="dropdown">
          <ListboxOptions
            :class="{ [`${COMPONENT_CLASS}__options`]: true, [`${COMPONENT_CLASS}__options--drop-up`]: dropUp }"
            @keydown="$event.preventDefault()"
          >
            <template v-if="placeholder">
              <ListboxOption v-slot="{ active, selected }" disabled as="template">
                <li
                  :class="[
                    [`${COMPONENT_CLASS}__option`],
                    [`${COMPONENT_CLASS}__option--disabled`],
                    {
                      [`${COMPONENT_CLASS}__option--active`]: active,
                      [`${COMPONENT_CLASS}__option--selected`]: selected || selectedOption === undefined
                    }
                  ]"
                >
                  <span :class="`${COMPONENT_CLASS}__option-text`">{{ placeholder }}</span>
                </li>
              </ListboxOption>
            </template>
            <ListboxOption
              v-for="(option, index) in options"
              v-slot="{ active, selected }"
              :key="index"
              :value="option"
              :disabled="option.disabled"
              as="template"
            >
              <li
                :class="{
                  [`${COMPONENT_CLASS}__option`]: true,
                  [`${COMPONENT_CLASS}__option--disabled`]: option.disabled,
                  [`${COMPONENT_CLASS}__option--active`]: active,
                  [`${COMPONENT_CLASS}__option--selected`]: selected
                }"
              >
                <span :class="`${COMPONENT_CLASS}__option-text`">{{ option.text }}</span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  </Component>
</template>

<style scoped lang="less">
@import "@dsk/styles/all";

.le-select {
  display: inline-block;
  position: relative;
  width: v-bind("width");
  color: @colors[@neutrals][500] !important; // !important can be removed once we no longer have bootstrap styling globally

  &--disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  &--small {
    .body-text("s");
    color: @colors[@neutrals][500] !important; // !important can be removed once we no longer have bootstrap styling globally
  }

  &--large {
    .body-text("m");
    color: @colors[@neutrals][500] !important; // !important can be removed once we no longer have bootstrap styling globally
  }

  &:focus-within {
    border-radius: 4px;
    .focus-outline(green);
  }

  &--has-error {
    &:focus-within {
      .focus-outline(red);
    }
  }

  &__container {
    display: inline-block;
    width: 100%;
  }

  &__label {
    margin-bottom: 2px;
    .body-text("xs");
    color: @colors[@neutrals][200];
  }

  &__button-text {
    flex: 1;
    margin-right: 6px;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__button-prefix-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 6px;

    + .le-select__button-text {
      padding-left: 1px;
    }
  }

  &__button {
    display: flex;
    position: relative;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    border: 1px solid @colors[@secondary][600];
    border-radius: 4px;
    background: @colors[@neutrals][100];

    &:hover {
      background: @colors[@secondary][200];
    }

    &.focus-visible {
      border-color: @colors[@primary][600];
      outline-offset: -1px;
    }

    &--small {
      padding: 5px 10px;
    }

    &--large {
      padding: 8px 12px;
    }

    &--has-error {
      border-color: @colors[@error][600];

      &.focus-visible {
        border-color: @colors[@error][600];
      }
    }
  }

  &__button-arrow-icon {
    transform: rotate(-180deg);
    transition: all 100ms linear;
  }

  &--open .le-select__button-arrow-icon {
    transform: rotate(0deg);
  }

  &--disabled .le-select__button {
    color: @colors[@secondary][600];
    cursor: default;
  }

  &--open:not(.le-select--has-error) .le-select__button {
    border-color: @colors[@primary][600];
  }

  &__options {
    display: flex;
    position: absolute;
    z-index: 999;
    flex-direction: column;
    width: 100%;
    max-width: 400px;
    max-height: v-bind("maxHeight");
    margin-top: 8px;
    margin-bottom: 0;
    padding: 8px;
    overflow-y: auto;
    list-style-type: none;
    border: 1px solid @colors[@secondary][600];
    border-radius: 4px;
    outline: none;
    background-color: @colors[@neutrals][100];
    box-shadow: @shadows[dropdown];
    gap: 6px;

    &:focus {
      outline: none;
    }

    &--drop-up {
      right: 0;
      bottom: 100%;
      left: 0;

      margin-top: 0;
      margin-bottom: 8px;
    }
  }

  &__option {
    .body-text("m");
    display: flex;
    align-items: center;
    width: 100%;
    padding: 2px 6px 2px 5px; // -1px for left, due to +1px in &__option-text
    border: 1px solid transparent;
    border-radius: 4px;
    color: @colors[@neutrals][500];
    text-align: left;
    cursor: pointer;

    &:hover {
      background-color: @colors[@secondary][200];
    }

    &--active {
      background-color: @colors[@secondary][200];
    }

    &--disabled {
      opacity: 0.3;
    }

    &--selected {
      border: 1px solid @colors[@secondary][600];
      background-color: @colors[@secondary][200];
    }
  }

  &__option-text {
    padding-left: 1px; // Avoid text cutting
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
